import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { ActionMenu, type ActionMenuItem } from '@/nova/components';

import { DotField } from '../../../customizer/components/DotField';
import type { EnabledKitSize, KitBox, KitQA, KitQAReason, KitRender } from '../../banner-kit-api';
import { LumioMark } from '../LumioMark';
import { ReviewBadgeTip } from './ReviewBadgeTip';
import * as L from '../KitLayerEditor/KitLayerEditor.styles';
import { sizeLabel } from './KitPicker';
import * as S from './BannerKitComposer.styles';
import { KIT_GRID_GAP_PX, KIT_TILE_MIN_W_PX } from './BannerKitComposer.styles';

/** A stable per-size seed for the generating dot lattice, so tiles drawing at once do not drift in sync. Pure. */
const sizeSeed = (id: string): number => {
  let s = 0;
  for (let i = 0; i < id.length; i += 1) s = (s + id.charCodeAt(i) * (i + 1)) % 100000;
  return s;
};

/** The "Needs review" tooltip: what the check actually found on the size (literal `t()` per reason, so each is
 *  extracted). A render made before reasons were recorded gets the general line. */
const reviewReason = (reason: KitQAReason | undefined, t: TFunction): string => {
  switch (reason) {
    case 'copy':
      return t('The check could not read the copy back exactly on this size. Look it over, or generate a new version.');
    case 'logo_drawn':
      return t('The picture may carry a drawn logo or brand name. Look it over, or generate a new version.');
    case 'duplicate_subject':
      return t('The main subject may show more than once on this size. Look it over, or generate a new version.');
    case 'logo_area_copy':
      return t('Some copy sits where the logo goes on this size. Look it over, or generate a new version.');
    case 'logo_area_busy':
      return t('The spot where the logo goes is busy on this size. Look it over, or generate a new version.');
    case 'unavailable':
      return t('The automatic check could not run on this size. Look it over before you use it.');
    case 'logo_missing':
      return t('Your logo could not be placed on this size. Generate a new version to try again.');
    default:
      return t('The automatic check was not sure about this size. Look it over, or generate a new version.');
  }
};

/** Whether two normalised [l, t, r, b] boxes share any area (touching edges do not count). Pure. */
const boxesOverlap = (a: KitBox, b: KitBox): boolean => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];

/** A render kept for review only because copy sat in the reserved logo area, whose placed logo (its stored box) touches
 *  none of the headline, sub-line and CTA: nothing collided, so the owner is not asked to review it. The same rule
 *  spark-creatives applies to new renders (logoAreaResolved); this covers renders stored before it. Pure. */
export const logoClearOfCopy = (qa: KitQA): boolean => {
  if (qa.reason !== 'logo_area_copy') return false;
  const logo = qa.boxes?.logo;
  if (!logo) return false;
  const copyKeys = ['headline', 'subhead', 'cta'] as const;
  return !copyKeys.some((key) => {
    const box = qa.boxes?.[key];
    return box ? boxesOverlap(box, logo) : false;
  });
};

/** One enabled size as the grid shows it. */
export interface SizeGridTile {
  size: EnabledKitSize;
  /** The shape this size is rendered from in the CURRENT document (the id a redraw targets). */
  shapeId: string;
  /** The selected version's render for this size, if any. */
  render?: KitRender;
  /** The key under the version's `shapes` the render was found under (its layers live there). */
  renderShape?: string;
  /** A run is rendering this size right now. */
  pending: boolean;
  /** The separation of this render into layers started this session: in flight, or why it failed. */
  layerRun?: { pending: boolean; error?: string };
}

export interface SizeGridProps {
  tiles: SizeGridTile[];
  /** Whether a rendered size may be downloaded (no run filling this version). */
  canDownload: boolean;
  /** The size whose download is being prepared. */
  downloadingSize?: string | null;
  onDownload: (tile: SizeGridTile) => void;
  /** Whether a render may be opened in the layer editor (flag, backend capability, desktop, no run starting). */
  canEditLayers?: boolean;
  /** Open the layer editor on a rendered tile, or separate its layers first. */
  onEditLayers?: (tile: SizeGridTile) => void;
}

/** A strip this wide (or wider) leaves the masonry for the strips band under it, where it stays readable. */
const WIDE_RATIO = 3;

/** The caption's height, counted into a card's height when dealing tiles into columns (S.TileCaption min-height + its
 *  border). */
const CAPTION_PX = 37;

/** How many masonry columns fit `width` (at least one), each at least a tile's minimum width. Pure. */
export const masonryColumnCount = (width: number): number =>
  Math.max(1, Math.floor((width + KIT_GRID_GAP_PX) / (KIT_TILE_MIN_W_PX + KIT_GRID_GAP_PX)));

/** Deal items into `count` balanced columns, in order, each into the currently shortest column (a card's height = the
 *  column width over its aspect ratio, plus its caption). Order reads top-down within a column. Pure. */
export const masonryColumns = <T,>(
  items: readonly T[],
  ratioOf: (item: T) => number,
  count: number,
  columnWidth: number,
): T[][] => {
  const columns: T[][] = Array.from({ length: Math.max(1, count) }, () => []);
  const heights = columns.map(() => 0);
  items.forEach((item) => {
    let shortest = 0;
    for (let i = 1; i < heights.length; i += 1) if (heights[i] < heights[shortest] - 0.5) shortest = i;
    columns[shortest].push(item);
    heights[shortest] += columnWidth / Math.max(0.05, ratioOf(item)) + CAPTION_PX + KIT_GRID_GAP_PX;
  });
  return columns;
};

/** The element's content width, measured before the first paint and kept in step with resizes. */
const useWidth = (ref: React.RefObject<HTMLElement>): number => {
  const [width, setWidth] = React.useState(0);
  // Syncs with the DOM: the grid's width (first read before paint, so the columns never jump).
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
};

/**
 * The kit's size grid: one card per enabled size in a balanced masonry, each banner at its own aspect ratio across its
 * column's full width (the ultra-wide strips in a band of their own below). A card shows its shape's render cropped
 * exactly as the export crops it, a spinner while the run is rendering it, the failure when it could not be
 * rendered (refunded), or a quiet placeholder before the first run. A "Needs review" badge flags a render whose
 * QA check found something worth a look (its reason in a tooltip). Each rendered tile has a menu:
 * instruction, download that size, or (where layer editing is on) edit its layers. A tile whose render is being
 * separated into layers says so over the render, and says why when that failed.
 */
export const SizeGrid = ({
  tiles,
  canDownload,
  downloadingSize,
  onDownload,
  canEditLayers = false,
  onEditLayers,
}: SizeGridProps): React.ReactElement => {
  const { t } = useTranslation();
  const gridRef = React.useRef<HTMLDivElement>(null);
  const width = useWidth(gridRef);
  const ratioOf = (tile: SizeGridTile): number => tile.size.w / tile.size.h;
  const columnTiles = tiles.filter((tile) => ratioOf(tile) < WIDE_RATIO);
  const stripTiles = tiles.filter((tile) => ratioOf(tile) >= WIDE_RATIO);
  // Before the first measure (no layout yet): three columns, the composer's usual width.
  const count = width > 0 ? masonryColumnCount(width) : 3;
  const columnWidth = width > 0 ? (width - KIT_GRID_GAP_PX * (count - 1)) / count : KIT_TILE_MIN_W_PX;
  const columns = masonryColumns(columnTiles, ratioOf, Math.min(count, Math.max(1, columnTiles.length)), columnWidth);

  const renderTile = (tile: SizeGridTile): React.ReactElement => {
    const { size, render, pending, layerRun } = tile;
    const label = sizeLabel(size);
    const ratio = ratioOf(tile);
    const url = render?.url;
    const failed = !url && Boolean(render?.error);
    const needsReview = Boolean(url && render?.qa && render.qa.status !== 'ok' && !logoClearOfCopy(render.qa));
    const separating = Boolean(layerRun?.pending && !pending);
    const items: ActionMenuItem[] = [];
    if (canEditLayers && onEditLayers && url && tile.renderShape && !layerRun?.pending) {
      items.push({ label: t('Edit layers'), icon: 'edit', onSelect: () => onEditLayers(tile) });
    }
    if (canDownload && url) {
      items.push({
        label: downloadingSize === size.id ? t('Preparing…') : t('Download this size'),
        icon: 'download',
        disabled: Boolean(downloadingSize),
        onSelect: () => onDownload(tile),
      });
    }
    return (
      <S.Tile key={size.id}>
        <S.TileFrame $ratio={ratio}>
          {url ? <S.TileImg src={url} alt={t('The {{size}} banner', { size: label })} loading="lazy" /> : null}
          {pending ? null : failed ? (
            <S.TileCenter title={render?.error}>
              <S.TileNote $tone="danger" text="Couldn't render this size" />
            </S.TileCenter>
          ) : url ? null : (
            <S.TileCenter>
              <S.TileNote text="Not generated yet" />
            </S.TileCenter>
          )}
        </S.TileFrame>
        {/* Generating / separating: the dot lattice across the whole card, the caption still readable on top. */}
        {pending ? (
          <S.TileGen aria-live="polite">
            <DotField seed={sizeSeed(size.id)} />
            <S.TileGenInner>
              {/* Lumio draws this size: the Lumio mark, never a sparkle. */}
              <LumioMark size={18} />
              <S.TileGenText text="Generating…" />
            </S.TileGenInner>
          </S.TileGen>
        ) : separating ? (
          <S.TileGen aria-live="polite">
            <DotField seed={sizeSeed(`${size.id}:layers`)} />
            <S.TileGenInner>
              <LumioMark size={18} />
              <S.TileGenText text="Separating layers…" />
            </S.TileGenInner>
          </S.TileGen>
        ) : null}
        <S.TileCaption $busy={pending || separating}>
          <S.TileSize raw text={label} />
          {needsReview ? (
            <ReviewBadgeTip id={`review-${size.id}`} reason={reviewReason(render?.qa?.reason, t)} />
          ) : null}
          {layerRun?.error && !layerRun.pending ? <L.TileIssue raw text={layerRun.error} /> : null}
          {items.length > 0 && !pending ? (
            <S.TileMenu>
              <ActionMenu items={items} align="end" label={t('Actions for {{size}}', { size: label })} />
            </S.TileMenu>
          ) : null}
        </S.TileCaption>
      </S.Tile>
    );
  };

  return (
    <div ref={gridRef}>
      {columnTiles.length > 0 ? (
        <S.Masonry>
          {columns.map((column, i) => (
            // Columns are positional (re-dealt on resize); the cards inside keep their own keys.
            // eslint-disable-next-line react/no-array-index-key
            <S.MasonryColumn key={i}>{column.map(renderTile)}</S.MasonryColumn>
          ))}
        </S.Masonry>
      ) : null}
      {stripTiles.length > 0 ? <S.Strips>{stripTiles.map(renderTile)}</S.Strips> : null}
    </div>
  );
};

export default SizeGrid;
